import { isNotEmptyString } from 'src/app/constant/constant-function';
import { COOKIE_KEYS } from 'src/app/constant/constant';
import { CookieService } from 'ngx-cookie-service';
import { Router, NavigationEnd } from '@angular/router';
import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { Subject, fromEvent, merge, timer, Subscription } from 'rxjs';
import { switchMap, throttleTime, takeUntil, tap, filter } from 'rxjs/operators';
import { LoginService } from 'src/app/services/login.service';
import { AuthGuardService } from 'src/app/services/auth-guard.service';
import { ToastrService } from 'ngx-toastr';
import { SESSION_LOGOUT } from 'src/app/constant/responseMessage';

@Injectable({
  providedIn: 'root',
})
export class InactivityService implements OnDestroy {

  private readonly inactivityTimeout = 30 * 60 * 1000; // 30 minutes
  private readonly activityThrottleTime = 2 * 1000;   // 2 seconds
  private readonly ACTIVITY_KEY = 'AUTH_APP_LAST_ACTIVITY';

  private isMonitoring = false;
  private activitySource$ = new Subject<void>();
  private stopMonitoring$ = new Subject<void>();
  private timerSubscription: Subscription | null = null;

  private userActivityEvents$ = merge(
    fromEvent(window, 'mousemove'),
    fromEvent(window, 'mousedown'),
    fromEvent(window, 'click'),
    fromEvent(window, 'keydown'),
    fromEvent(window, 'touchstart'),
    fromEvent(window, 'scroll')
  ).pipe(
    throttleTime(this.activityThrottleTime)
  );

  public onTimeout$ = new Subject<void>();

  constructor(
    private router: Router,private cookieService: CookieService, 
    private ngZone: NgZone,
    private authGuard: AuthGuardService,
    private loginAPI: LoginService,
    private toastr: ToastrService
  ) {
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => {
      const isAuth = (this as any).authGuard && (this as any).authGuard.isAuthorized && (this as any).authGuard.isAuthorized();
      if (isAuth) {
        if (!this.isMonitoring) this.startMonitoring();
      } else {
        if (this.isMonitoring) this.stopMonitoring();
      }
    });
  }

  public startMonitoring(): void {
    this.isMonitoring = true;
    this.stopMonitoring$.next();
    this.updateLastActivity();

    this.userActivityEvents$
      .pipe(
        filter(() => isNotEmptyString(this.cookieService.get(COOKIE_KEYS.USER_ID))),
        tap(() => this.resetTimer()),
        takeUntil(this.stopMonitoring$)
      )
      .subscribe();

    fromEvent<StorageEvent>(window, 'storage')
      .pipe(takeUntil(this.stopMonitoring$))
      .subscribe((event) => {
        if (event.key === this.ACTIVITY_KEY) {
          this.activitySource$.next();
        }
      });

    this.timerSubscription = this.activitySource$
      .pipe(
        filter(() => isNotEmptyString(this.cookieService.get(COOKIE_KEYS.USER_ID))),
        switchMap(() => timer(this.inactivityTimeout)),
        takeUntil(this.stopMonitoring$)
      )
      .subscribe(() => {
        this.checkAndLogout();
      });
  }

  public stopMonitoring(): void {
    this.isMonitoring = false;
    this.stopMonitoring$.next();
    if (this.timerSubscription) {
      this.timerSubscription.unsubscribe();
      this.timerSubscription = null;
    }
  }

  public resetTimer(): void {
    this.updateLastActivity();
    this.activitySource$.next();
  }

  private updateLastActivity(): void {
    localStorage.setItem(this.ACTIVITY_KEY, Date.now().toString());
  }

  private checkAndLogout(): void {
    const lastActivity = Number(localStorage.getItem(this.ACTIVITY_KEY) || 0);
    const now = Date.now();

    if (now - lastActivity >= this.inactivityTimeout) {
      this.ngZone.run(() => {
        this.onTimeout$.next();
        this.stopMonitoring();
        if ((this as any).authGuard && (this as any).authGuard.isAuthorized) {
            if ((this as any).authGuard.isAuthorized()) {
                this.triggerLogout();
            }
        } else {
            this.triggerLogout();
        }
      });
    } else {
      this.activitySource$.next();
    }
  }

  private triggerLogout(): void {
    this.logout();
  }

  logout(): void {
    this.loginAPI.logout().subscribe(() => {
      this.toastr.info(SESSION_LOGOUT);
      this.authGuard.logout();
    });
  }

  ngOnDestroy(): void {
    this.stopMonitoring();
    this.stopMonitoring$.complete();
    this.activitySource$.complete();
    this.onTimeout$.complete();
  }
}
